import WebLogin from "@/interfaces/WebLogin/WebLogin";
import type WebLoginAuthenticationTokens from "@/interfaces/WebLogin/WebLoginAuthenticationTokens";

export function getAccountNameInitials(fullName: string): string {
  if (!fullName) return "";

  const namePartArray = fullName.split(" ");

  let initials = "";
  for (const namePart of namePartArray) {
    if (initials.length < 2) {
      if (/^[a-zA-Z]+$/.test(namePart[0])) {
        initials += namePart[0];
      }
    }
  }

  return initials.toUpperCase();
}

// A token part is base64url of UTF-8 text, which atob alone reads as base64 of single bytes.
function decodeJsonWebTokenPart(part: string): string {
  const binary = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
  return new TextDecoder().decode(Uint8Array.from(binary, (character) => character.charCodeAt(0)));
}

export function getWebLoginFromJsonWebToken(jwt: string): WebLogin {
  const jwtParts = jwt.split(".");

  if (jwtParts.length < 2 || !jwtParts[1]) {
    throw new Error("JWT payload is missing.");
  }

  let jwtParsed: Record<string, string>;

  try {
    jwtParsed = JSON.parse(decodeJsonWebTokenPart(jwtParts[1])) as Record<string, string>;
  } catch (error) {
    throw new Error(
      `JWT payload is invalid: ${
        error instanceof Error ? error.message : "Unknown parsing error."
      }`
    );
  }

  return {
    webLoginId: jwtParsed.webLoginId,
    webLoginUserName: jwtParsed.webLoginUserName,
    keepLoggedIn: jwtParsed.keepLoggedIn === "True",
    isEmployee: jwtParsed.isEmployee === "True",
    employeeSecurity: parseInt(jwtParsed.employeeSecurity),
    employeeCustomerAccess: parseInt(jwtParsed.employeeCustomerAccess),
    employeeManagerName: jwtParsed.employeeManagerName,
    forcePasswordChange: jwtParsed.forcePasswordChange === "True",
  } as WebLogin;
}

// When a JSON web token expires, in seconds since 1970; 0 when it cannot be read.
export function getJsonWebTokenExpiry(jwt: string): number {
  try {
    return Number((JSON.parse(decodeJsonWebTokenPart(jwt.split(".")[1])) as { exp?: number }).exp ?? 0);
  } catch {
    return 0;
  }
}

export function isSameWebLogin(firstJwt: string, secondJwt: string): boolean {
  if (firstJwt === secondJwt) {
    return true;
  }

  try {
    return (
      getWebLoginFromJsonWebToken(firstJwt).webLoginId ===
      getWebLoginFromJsonWebToken(secondJwt).webLoginId
    );
  } catch {
    return false;
  }
}

// Whether the session kept in local storage, shared by kept tabs, is this login's (and not another login's).
export function isKeptSessionOf(storedTokensJson: string | null, jsonWebToken: string): boolean {
  try {
    const storedTokens = storedTokensJson ? (JSON.parse(storedTokensJson) as WebLoginAuthenticationTokens) : undefined;
    return !!storedTokens && isSameWebLogin(storedTokens.jsonWebToken, jsonWebToken);
  } catch {
    return false;
  }
}

// Tabs kept logged in share one session in local storage, and a refresh replaces its refresh token, so tokens stored
// there for the same login that expire later than these were refreshed by another tab and are the ones to use.
export function getNewerKeptSessionTokens(
  tokens: WebLoginAuthenticationTokens
): WebLoginAuthenticationTokens | undefined {
  try {
    const storedTokensJson = localStorage.getItem("tokens");
    if (!storedTokensJson) {
      return undefined;
    }

    const storedTokens = JSON.parse(storedTokensJson) as WebLoginAuthenticationTokens;
    const isNewer =
      storedTokens.refreshToken !== tokens.refreshToken &&
      getJsonWebTokenExpiry(storedTokens.jsonWebToken) > getJsonWebTokenExpiry(tokens.jsonWebToken);

    return isNewer && isSameWebLogin(storedTokens.jsonWebToken, tokens.jsonWebToken)
      ? storedTokens
      : undefined;
  } catch {
    return undefined;
  }
}
